// Hashed user data for the client-side ad pixels GTM fans out to on `sign_up`
// (Google Ads enhanced conversions today; LinkedIn/X/Reddit can reuse it).
//
// Recipe = the landing's `src/lib/hash.ts`: SHA-256 hex of the trimmed,
// lower-cased email. One email therefore hashes identically on founding.dev
// and app.founding.dev, which is what lets Google match an app sign-up back
// to the ad click. The raw email never enters the dataLayer.
//
// Everything here is best-effort: no SubtleCrypto (very old browsers, some
// insecure contexts) or a thrown digest just means "no user_data" — the
// `sign_up` push itself must still happen.

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export async function sha256Hex(input: string): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/**
 * Google's email normalisation differs from Meta's in one case: for
 * gmail.com / googlemail.com addresses Google also removes every dot in the
 * local part before hashing ("first.last@gmail.com" → "firstlast@gmail.com"),
 * because Gmail ignores dots. Meta, LinkedIn, X and Reddit hash the dotted
 * form. One hash cannot satisfy both, so `em` stays Meta's form and
 * `em_google` carries Google's; GTM's "cjs - Google Ads user_data" variable
 * prefers `em_google`. Plus-aliases are kept: Google does not strip them.
 * Same recipe as the landing's `src/lib/hash.ts`.
 */
export function normalizeEmailForGoogle(email: string): string {
  const e = email.trim().toLowerCase().replace(/\s+/g, '');
  const at = e.lastIndexOf('@');
  if (at < 0) return e;
  const local = e.slice(0, at);
  const domain = e.slice(at + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') return `${local.replace(/\./g, '')}@${domain}`;
  return e;
}

export interface SignupUserData {
  /** SHA-256 hex of the normalised email — Meta's form, shared by LinkedIn/X/Reddit. */
  em: string;
  /** SHA-256 hex of Google's normalised form (gmail dots removed) — Google Ads enhanced conversions. */
  em_google?: string;
}

export async function hashedEmailUserData(
  email: string | null | undefined,
): Promise<SignupUserData | undefined> {
  if (typeof email !== 'string' || !email.trim()) return undefined;
  const em = await sha256Hex(normalizeEmail(email));
  if (!em) return undefined;
  const emGoogle = await sha256Hex(normalizeEmailForGoogle(email));
  return emGoogle && emGoogle !== em ? { em, em_google: emGoogle } : { em, em_google: em };
}
